import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import PlanLayout from "./components/PlanLayout";
import StatStrip from "../../Component/StatStrip";
import Loader from "../../Component/Loader/Loader";
import { postRequest } from "../../utils/api";
import useMetaPricing, { CATS, CatId, Markups, emptyMarkup } from "./components/useMetaPricing";

const r2 = (n: number) => Math.round(n * 100) / 100;

const MetaPricing = () => {
    const { loading, setLoading, countries, countryCode, setCountryCode, plans, base, markups, setMarkups, reload } = useMetaPricing();
    const [selPlanId, setSelPlanId] = useState<string>('');

    useEffect(() => {
        if (!selPlanId && plans.length) setSelPlanId(plans[0]._id);
    }, [plans, selPlanId]);

    const handleSave = async () => {
        setLoading(true);
        try {
            // Markups are uniform across every country — no countryCode in the payload.
            const response: any = await postRequest(`/plans/update-markups`, { markups });
            if (response.status === "success") {
                toast.success('Message pricing saved successfully');
                reload();
            } else {
                toast.error(response.message || "Failed to save pricing.");
            }
        } catch (error) {
            toast.error("An error occurred while saving.");
        } finally {
            setLoading(false);
        }
    };

    const handleReset = () => {
        const reset: Markups = {};
        plans.forEach(p => { reset[p._id] = emptyMarkup(); });
        setMarkups(reset);
        toast.info("Reset to defaults");
    };

    const setCatMarkup = (planId: string, catId: CatId, pct: number) =>
        setMarkups(prev => ({ ...prev, [planId]: { ...(prev[planId] || emptyMarkup()), [catId]: pct } }));

    // Client price edit reverse-calculates the markup % for that category, against whichever
    // country's base rate is currently selected — the resulting % then applies to every country.
    const handleSpecificPriceEdit = (planId: string, catId: CatId, basePrice: number, newPriceStr: string) => {
        const newPrice = parseFloat(newPriceStr.replace(/[^\d.-]/g, ''));
        if (isNaN(newPrice) || basePrice === 0) return;
        setCatMarkup(planId, catId, ((newPrice - basePrice) / basePrice) * 100);
    };

    const sel = plans.find(p => p._id === selPlanId);
    const selMk = markups[selPlanId] || emptyMarkup();
    const selCountryName = countries.find(c => c.cmp_country_code === countryCode)?.cmp_country_name || countryCode;

    return (
        <>
            {loading && <Loader />}
            <PlanLayout actions={<>
                <select
                    className="cx-btn cx-btn-light"
                    value={countryCode}
                    onChange={(e) => setCountryCode(e.target.value)}
                    aria-label="Preview country"
                    style={{ paddingRight: 28 }}
                >
                    {countries.length === 0 && <option value={countryCode}>{countryCode}</option>}
                    {countries.map(c => (
                        <option key={c.cmp_country_code} value={c.cmp_country_code}>{c.cmp_country_name}</option>
                    ))}
                </select>
                <button type="button" className="cx-btn cx-btn-light" onClick={handleReset}><i className="ri-arrow-go-back-line"></i>Reset</button>
                <button type="button" className="cx-btn cx-btn-primary" onClick={handleSave}><i className="ri-check-line"></i>Save pricing</button>
            </>}>
                <StatStrip items={[
                    { label: "Pricing model", value: "Per-message", icon: "ri-message-2-line", delta: "Meta · by category" },
                    { label: "Market", value: `${selCountryName} · ₹`, icon: "ri-map-pin-line", delta: "Base rates in INR" },
                    { label: "Marketing base", value: `₹${base('cmp_meta_marketing_price').toFixed(4)}`, icon: "ri-megaphone-line", delta: "per message" },
                    { label: "Service base", value: `₹${base('cmp_meta_service_price').toFixed(4)}`, icon: "ri-customer-service-2-line", delta: "per message · billable from Oct 1, 2026" },
                ]} />

                <div className="cx-card">
                    <div className="cx-card-h">
                        <h6>Markup by plan</h6>
                        <span className="sub">Select a plan to edit its pricing below · markups apply to every country, not just {selCountryName}</span>
                    </div>
                    <div className="cx-card-b">
                        <div className="cx-mk-grid">
                            {plans.map(p => (
                                <div role="button" tabIndex={0} key={p._id} className={`cx-mk ${selPlanId === p._id ? 'on' : ''}`} onClick={() => setSelPlanId(p._id)} onKeyDown={(e) => { if (e.key === 'Enter' && e.target === e.currentTarget) setSelPlanId(p._id); }}>
                                    <span>{p.ppm_plan_title}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                    <div className="cx-tbl-wrap">
                        <table className="cx-tbl">
                            <thead>
                                <tr>
                                    <th>Category</th>
                                    <th className="r">Meta base ({selCountryName})</th>
                                    <th className="r">Markup</th>
                                    <th className="r">Client price{sel ? ` · ${sel.ppm_plan_title}` : ''}</th>
                                    <th className="r">Your margin / msg</th>
                                </tr>
                            </thead>
                            <tbody>
                                {CATS.map(cat => {
                                    const b = base(cat.key);
                                    const pct = selMk[cat.id] || 0;
                                    const client = b * (1 + pct / 100);
                                    return (
                                        <tr key={cat.id}>
                                            <td><b style={{ fontWeight: 600 }}>{cat.name}</b><div className="fs12 muted">{cat.desc}</div></td>
                                            <td className="r mono muted">₹{b.toFixed(4)}</td>
                                            <td className="r">
                                                <span className="cx-mk-in" style={{ justifyContent: 'flex-end' }}>
                                                    <input
                                                        key={`${selPlanId}-${cat.id}-${r2(pct)}`}
                                                        type="number"
                                                        min="0"
                                                        defaultValue={r2(pct)}
                                                        disabled={!sel}
                                                        onBlur={(e) => {
                                                            const v = Math.max(0, parseFloat(e.target.value) || 0);
                                                            // tabbing through must not round away a reverse-calculated markup
                                                            if (v !== r2(pct)) setCatMarkup(selPlanId, cat.id, v);
                                                        }}
                                                        aria-label={`${cat.name} markup`}
                                                    />%
                                                </span>
                                            </td>
                                            <td className="r">
                                                <span className="mono" style={{ color: 'var(--wa-secondary)', fontWeight: 600 }}>₹</span>
                                                <input
                                                    key={`${selPlanId}-${cat.id}-${client.toFixed(4)}`}
                                                    className="cx-rate-in"
                                                    style={{ color: 'var(--wa-secondary)', fontWeight: 600, marginLeft: 4 }}
                                                    defaultValue={client.toFixed(4)}
                                                    disabled={!sel}
                                                    onBlur={(e) => { if (e.target.value !== client.toFixed(4)) handleSpecificPriceEdit(selPlanId, cat.id, b, e.target.value); }}
                                                    aria-label={`${cat.name} client price`}
                                                />
                                            </td>
                                            <td className="r mono up">+₹{(client - b).toFixed(4)}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                    <div className="cx-tfoot">
                        <span>Edit the <b>markup %</b> or type a <b>client price</b> directly — the other one is recalculated, and it applies to this plan across every country.</span>
                        <span>Base rates shown for: <b>{selCountryName}</b></span>
                    </div>
                </div>
            </PlanLayout>
        </>
    );
};

export default MetaPricing;
